import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

export default function ContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setSuccess(true);
      toast.success("Message sent successfully");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send message. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="contact-success"
        className="rounded-sm border border-[#1F6B5C]/30 bg-[#1F6B5C]/5 p-8 text-center"
      >
        <CheckCircle2 className="h-12 w-12 text-[#1F6B5C] mx-auto" />
        <h3 className="font-display text-2xl font-bold text-[#0F172A] mt-4">
          Message sent
        </h3>
        <p className="text-[#475569] mt-2">
          Thank you for reaching out. We will respond shortly.
        </p>
        <Button
          onClick={() => {
            setSuccess(false);
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
          }}
          className="mt-6 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-sm"
          data-testid="contact-new-button"
        >
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className="grid grid-cols-1 md:grid-cols-2 gap-5"
    >
      <div>
        <Label htmlFor="name">Full Name *</Label>
        <Input
          id="name"
          data-testid="contact-input-name"
          value={form.name}
          onChange={update("name")}
          className="mt-2"
          required
        />
      </div>
      <div>
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          data-testid="contact-input-email"
          value={form.email}
          onChange={update("email")}
          className="mt-2"
          required
        />
      </div>
      <div>
        <Label htmlFor="phone">Phone</Label>
        <Input
          id="phone"
          type="tel"
          data-testid="contact-input-phone"
          value={form.phone}
          onChange={update("phone")}
          className="mt-2"
        />
      </div>
      <div>
        <Label htmlFor="subject">Subject *</Label>
        <Input
          id="subject"
          data-testid="contact-input-subject"
          value={form.subject}
          onChange={update("subject")}
          className="mt-2"
          required
        />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="message">Message *</Label>
        <Textarea
          id="message"
          rows={5}
          data-testid="contact-input-message"
          value={form.message}
          onChange={update("message")}
          className="mt-2"
          required
        />
      </div>
      <div className="md:col-span-2">
        <Button
          type="submit"
          disabled={submitting}
          data-testid="contact-submit-button"
          className="w-full sm:w-auto bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-sm px-8 h-12"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Sending…
            </>
          ) : (
            "Send Message"
          )}
        </Button>
      </div>
    </form>
  );
}
